import {
  SUPPORT_PHONE,
  SUPPORT_TEL,
  SUPPORT_WHATSAPP,
  SUPPORT_WHATSAPP_DISPLAY,
} from "./market";

/**
 * The Lusuku promise: the one place its words live.
 *
 * The homepage banner, every cart and the order screens read from here, so a
 * change to the promise (or to the refund terms behind it) is one edit and
 * every surface says the same thing.
 *
 * Only what operations stand behind is stated. Cash on delivery has product
 * and basket ceilings (codPolicyService on the server), so it says "most
 * orders". Refund timings come from REFUND_TERMS below.
 */

/*
 * Refund and return terms, tightened for quick resolution (2 Nov 2026: the
 * founder asked for shorter refund and return timelines). The Return & Refund
 * Policy page, the homepage banner and every cart read these, so changing a
 * number here changes it everywhere at once. Each one is a commitment the
 * support team has to be staffed to keep.
 */
export const REFUND_TERMS = {
  /* Report a wrong, damaged, missing or poor-quality item (was 7 days). */
  reportWindow: "48 hours",
  /* First reply to a reported problem (was 24 hours). */
  firstReply: "2 hours",
  /* Prepaid item handed back to the rider at the door (was 24 hours). */
  doorRejectionRefund: "2 hours",
  /* Mobile money and cash-on-delivery refunds once approved (was 3 to 5 business days). */
  mobileMoneyRefund: "24 hours",
  /* Card refunds are sent within this time; the bank then takes its own. */
  cardRefundSent: "24 hours",
  cardBankTime: "5 to 10 business days",
  policyUpdated: "November 2, 2026",
};

export const REFUND_SUMMARY = `Report a problem within ${REFUND_TERMS.reportWindow} and we reply within ${REFUND_TERMS.firstReply}. MoMo refunds are sent within ${REFUND_TERMS.mobileMoneyRefund} of approval.`;

export const LUSUKU_PROMISE_HEADLINE = "Your market, at your door.";
export const LUSUKU_PROMISE_MOBILE_HEADLINE = "Your marketplace, at your door.";

export const LUSUKU_PROMISE_SUMMARY =
  "Restaurants, shops, pharmacies and brands you know, delivered to you. Order in minutes and pay on delivery or online.";

/**
 * The subhead with the server's live vendor count in it ("40+ restaurants,
 * shops, ..."): a specific number persuades where a vague claim does not.
 * The count comes from GET /api/growth/home-facts, already rounded down and
 * null below its floor, and the plain wording stands in whenever there is
 * none.
 */
export const promiseSummaryFor = (vendorCount) =>
  Number(vendorCount) > 0
    ? `${Number(vendorCount)}+ restaurants, shops, pharmacies and brands you know, delivered to you. Order in minutes and pay on delivery or online.`
    : LUSUKU_PROMISE_SUMMARY;

/*
 * Each point leads with the shopper's gain, worded for the doubt it answers
 * (3 Nov 2026):
 *
 *   pay_on_delivery  risk reversal: nothing is paid before the goods arrive.
 *                    "Everyday orders", not a number: cash has both a basket
 *                    and an item ceiling (codPolicyService), and one number
 *                    would be wrong for the other.
 *   check_at_door    control: the shopper decides at the door, not after.
 *   refund_same_way  specificity: two real times, the first reply and the
 *                    MoMo refund, both from REFUND_TERMS.
 */
export const LUSUKU_PROMISE_POINTS = [
  {
    key: "pay_on_delivery",
    title: "Pay when it arrives",
    detail: "Cash at your door for everyday orders, or MoMo and card",
  },
  {
    key: "check_at_door",
    title: "Check it before you accept",
    detail: `Hand back anything wrong. Prepaid refunds within ${REFUND_TERMS.doorRejectionRefund}`,
  },
  {
    key: "refund_same_way",
    title: "Quick refunds",
    detail: `We reply in ${REFUND_TERMS.firstReply}. MoMo refunds within ${REFUND_TERMS.mobileMoneyRefund} of approval`,
  },
];

export const REFUND_POLICY_PATH = "/return-policy";
export const REFUND_POLICY_LINK_TEXT = "How refunds work";

/* Ordering by phone: the alternative for anyone who would rather talk to a
   person than finish the order on screen. */
export const HELPLINE_DISPLAY = SUPPORT_PHONE;
export const HELPLINE_TEL = SUPPORT_TEL;
export const HELPLINE_ORDER_TEXT = "Prefer to order by phone?";

export const SUPPORT_WHATSAPP_URL = SUPPORT_WHATSAPP
  ? `https://wa.me/${SUPPORT_WHATSAPP}`
  : null;
export const SUPPORT_WHATSAPP_LABEL = SUPPORT_WHATSAPP_DISPLAY;

/* Ordering on WhatsApp, offered beside the phone number: the channel most
   shoppers already use, opened with the first line typed for them so asking
   costs one tap. */
export const WHATSAPP_ORDER_TEXT = "Chat on WhatsApp";
export const WHATSAPP_ORDER_MESSAGE =
  "Hello Lusuku, I would like to place an order.";
export const WHATSAPP_ORDER_URL = SUPPORT_WHATSAPP_URL
  ? `${SUPPORT_WHATSAPP_URL}?text=${encodeURIComponent(WHATSAPP_ORDER_MESSAGE)}`
  : null;

/*
 * The homepage's two buttons (3 Nov 2026). Which one leads is the server's
 * call (`leadAction` on GET /api/growth/home-facts: food around meal times,
 * groceries otherwise); a shopper with items waiting gets their cart in the
 * second place, because an unfinished basket pulls harder than a new aisle.
 */
export const HERO_ACTIONS = Object.freeze({
  FOOD: { key: "food", label: "Order food", to: "/restaurants" },
  GROCERIES: { key: "groceries", label: "Shop groceries", to: "/shop?page=1" },
});

const itemWord = (count) => (count === 1 ? "item" : "items");

/**
 * The two buttons, in order. Pure.
 *
 * @param {{ leadAction?: "FOOD"|"GROCERIES"|null, cartCount?: number }} args
 * @returns {{ key: string, label: string, title?: string, detail?: string, to: string }[]}
 */
export const heroActionsFor = ({ leadAction = null, cartCount = 0 } = {}) => {
  const lead = HERO_ACTIONS[leadAction] || HERO_ACTIONS.FOOD;
  const other =
    lead === HERO_ACTIONS.FOOD ? HERO_ACTIONS.GROCERIES : HERO_ACTIONS.FOOD;
  const count = Math.max(0, Math.floor(Number(cartCount) || 0));
  const second =
    count > 0
      ? {
          key: "cart",
          label: `Your cart (${count} ${itemWord(count)})`,
          title: "Your cart",
          detail: `(${count} ${itemWord(count)})`,
          to: "/cart",
        }
      : other;
  return [lead, second];
};
